import React, { useMemo } from 'react';
import T from 'prop-types';
import AliceCarousel from 'react-alice-carousel';
import classnames from 'classnames';
import {
  paperShape,
  frameShape
} from 'arts/reducers/work';
import Collapsible from 'arts/components/Collapsible';
import Frame from './frame';
// eslint-disable-next-line import/named
import Mounts, {
  FULL_BLEED, MATTE, FLOAT_DECKLED, FLOAT_STRIGHT
} from './mounts';
import SelectPlexiGlass from './select_plexi_glass';
import { aliceSliderSettins, handleDragStart } from '../slider_arrows';
import Warning from './warning';
import * as Classes from './styles.module.scss';

const hasMount = (options, mount, frameName) => !!options.find(
  i => i.mount === mount && i.frameName === frameName
);

const Paper = ({
  paper,
  setFrame,
  setNonGlare,
  selectedFrame,
  thumbnails,
  nonGlare,
  availableFrames,
  selectFirstAvailableFrame
}) => {
  const selectFrame = (frame) => setFrame(paper, frame);

  const setFrameAndMount = (frameName, mount, frameMatColorName) => {
    if (!(frameName && mount)) { return; }

    // with the selected mount
    let frame = paper.options.find(i => (
      i.mount === mount && i.frameName === frameName && i.frameMatColorName === frameMatColorName
    ));

    if (frame) {
      selectFrame(frame);
      return;
    }

    // prefer White mat color, then any mat color
    frame = paper.options.find(i => (
      i.mount === mount && i.frameName === frameName && i.frameMatColorName === 'White'
    )) || paper.options.find(i => (
      i.mount === mount && i.frameName === frameName
    ));
    if (frame) {
      selectFrame(frame);
      setNonGlare(false);
      return;
    }

    // any mount or mat color, prefer White
    frame = paper.options.find(i => i.frameName === frameName && i.frameMatColorName === 'White')
      || paper.options.find(i => i.frameName === frameName);
    if (frame) {
      selectFrame(frame);
      setNonGlare(false);
      return;
    }

    // any frame
    selectFirstAvailableFrame(paper);
  };

  const setFrameByName = (frameName) => {
    const { mount, frameMatColorName } = selectedFrame || {};
    setFrameAndMount(frameName, mount, frameMatColorName);
  };

  const selectMount = (mount, frameMatColorName) => {
    const { frameName } = selectedFrame || {};
    setFrameAndMount(frameName, mount, frameMatColorName);
  };

  const renderAvailableFrames = useMemo(() => {
    const categories = availableFrames
      .map((f) => f.frameCategory)
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort();

    return categories.map((category) => {
      const frames = availableFrames.filter((f) => f.frameCategory === category);
      const {
        frameName, frameDisplayWidth, frameDisplayDepth, warning, frameLabel, frameCategory
      } = selectedFrame || {};
      let frameDescription = null;
      let renderWarning = null;
      if (frameCategory === category) {
        frameDescription = ` - ${ frameName }, ${ frameLabel } width ${ frameDisplayWidth }${ window.currentLocale.unit }, depth ${ frameDisplayDepth }`;
        renderWarning = <Warning warning={ warning } />;
      }

      const filteredUniqFrames = frames.filter((frame, index, self) => self.findIndex(
        f => f.frameName === frame.frameName) === index
      );

      const renderedFrames = filteredUniqFrames.map((frame, index) => (
        <Frame
          key={ index }
          frameName={ frame.frameName }
          frameThumbUrl={ frame.frameThumbUrl }
          setFrameByName={ setFrameByName }
          selected={ frameName === frame.frameName }
          handleDragStart={ handleDragStart }
        />
      ));

      return (
        <div key={ category }>
          <div className={ Classes.category }>
            { category }
            <span>{ frameDescription }</span>
          </div>
          { renderWarning }
          <div className={ Classes.frames }>
            { renderedFrames }
          </div>
          <div className={ Classes.mobileFrames }>
            <AliceCarousel { ...aliceSliderSettins(false) }>
              { renderedFrames }
            </AliceCarousel>
          </div>
        </div>
      );
    });
  }, [availableFrames, selectedFrame]);

  const {
    frameName, isTapestry, mount, nonGlarePrice, frameMatColorName
  } = selectedFrame || {};

  const uid = paper.paperId;
  const collapsed = isTapestry ? false : (paper.options.indexOf(selectedFrame) <= 0);
  const { url: previewUrl } = (thumbnails || []).find((t) => t.type === 'framed') || {};
  const withMatte = hasMount(paper.options, MATTE, frameName) ? 1 : 0;
  const withFullBleed = hasMount(paper.options, FULL_BLEED, frameName) ? 1 : 0;
  const withDckl = hasMount(paper.options, FLOAT_DECKLED, frameName) ? 1 : 0;
  const withFltn = hasMount(paper.options, FLOAT_STRIGHT, frameName) ? 1 : 0;
  const matAndColors = paper.options.filter(
    f => f.mount === MATTE && f.frameName === frameName
  ).map(f => [f.frameMatColorName, f.frameMatColorRgb]);
  const matColors = matAndColors.map(f => f[0]);
  const matColorsRgb = matAndColors.map(f => f[1]);

  const hasStep2 = (withMatte + withFullBleed + withDckl + withFltn) > 1;

  const step2 = hasStep2 ? (
    <Mounts
      selectedMount={ mount }
      selectedMatColor={ frameMatColorName }
      onSelect={ selectMount }
      paperId={ paper.paperId }
      matte={ withMatte > 0 }
      fullBleed={ withFullBleed > 0 }
      dckl={ withDckl > 0 }
      fltn={ withFltn > 0 }
      matColors={ matColors }
      matColorsRgb={ matColorsRgb }
    />
  ) : null;

  const step3 = hasStep2 && nonGlarePrice ? (
    <div className={ Classes.step3 }>
      <div className={ Classes.sectionTitle }>
        <span>Step 3 - </span>
        Select UV Plexiglass
      </div>
      <SelectPlexiGlass nonGlare={ nonGlare } setNonGlare={ setNonGlare } />
    </div>
  ) : null;

  return (
    <Collapsible
      uid={ uid }
      key={ uid }
      collapsed={ collapsed }
    >
      <section className={ Classes.mouldingSection } data-testid="moulding-section">
        <div className={ classnames(Classes.sectionTitle, { [Classes.hidden]: !hasStep2 }) }>
          <span>Step 1 - </span>
          Select Moulding
        </div>
        { renderAvailableFrames }
      </section>

      <section className={ Classes.options }>
        <div className={ Classes.previewImage }>
          { previewUrl ? <img src={ previewUrl } alt="" /> : null }
        </div>
        <div>
          <div className={ classnames(Classes.sectionTitle, { [Classes.hidden]: !hasStep2 }) }>
            <span>Step 2 - </span>
            Select Mount
          </div>
          { step2 }
          { step3 }
        </div>
      </section>
    </Collapsible>
  );
};

Paper.propTypes = {
  paper: paperShape.isRequired,
  setFrame: T.func.isRequired,
  setNonGlare: T.func.isRequired,
  selectedFrame: frameShape,
  thumbnails: T.arrayOf(T.shape({
    type: T.string.isRequired,
    url: T.string.isRequired
  })).isRequired,
  nonGlare: T.bool.isRequired,
  availableFrames: T.array.isRequired,
  selectFirstAvailableFrame: T.func.isRequired
};

export default Paper;
